import { isEmpty, Text, VFlow } from 'bold-ui'
import { useFlags } from 'config/useFlagsContext'
import {
  AtendimentoProfissionalPreNatal,
  AtendimentoProfissionalPuericultura,
  EvolucaoObjetivo,
  HistoricoObjetivoPecFragmentFragment,
  MarcadorConsumoAlimentar,
  ResultadoExame,
} from 'graphql/types.generated'
import React from 'react'
import { isUndefinedOrNull } from 'util/checks'
import { calculateAge } from 'util/date/calculateAge'
import { filterByRegistroAnterior } from 'view/atendimentos/detail/historico/utils/filterByRegistroAnterior'
import {
  convertMedicoesToHistoricoMedicaoModel,
  getMedicoesPreNatal,
  hasAllAttrsNull,
  hasMedicoes,
  naoRegistradoNesseAtendimento,
} from 'view/atendimentos/detail/historico/utils/utils-historico'
import { ArquivoAtendimentoProfissionalHistorico } from 'view/atendimentos/detail/soap/objetivo/anexo-arquivos/model-anexoArquivos'
import { IVCF_RESULTADOS } from 'view/atendimentos/detail/soap/objetivo/ivcf/model-ivcf'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import { HistoricoMedicoesObservacaoPanel } from '../../atendimento-observacao/HistoricoMedicoesObservacaoPanel'
import HistoricoArquivosAnexadosTableBox from '../../HistoricoArquivosAnexadosTableBox'
import { HistoricoIvcfTableBox } from '../../HistoricoIvcfTableBox'
import HistoricoMCATableBox from '../../HistoricoMCATableBox'
import HistoricoMedicoesTableBox from '../../HistoricoMedicoesTableBox'
import HistoricoResultadoExamesTable from '../../HistoricoResultadoExamesTable'
import HistoricoMedicoesPreNatalTableBox from '../pre-natal/HistoricoMedicoesPreNatalTableBox'
import HistoricoMChatTableBox from '../puericultura/HistoricoMChatTableBox'
import HistoricoObjetivoInformacoesPuericultura from '../puericultura/HistoricoObjetivoInformacoesPuericultura'

type Ivcf = HistoricoObjetivoPecFragmentFragment['ivcf']

export type MChat = HistoricoObjetivoPecFragmentFragment['mChat']

export type AtendimentoProfissionalPreNatalModel = Pick<
  AtendimentoProfissionalPreNatal,
  'id' | 'gravidezPlanejada' | 'movimentacaoFetal' | 'tipoEdema'
>

export interface HistoricoObjetivoPecPanelProps {
  evolucaoObjetivo: EvolucaoObjetivo
  medicoes: HistoricoMedicaoModel[]
  isAtendObservacao?: boolean
  examesResultados?: ResultadoExame[]
  atendProfPreNatal?: AtendimentoProfissionalPreNatalModel
  atendProfPuericultura?: AtendimentoProfissionalPuericultura
  marcadorConsumoAlimentar?: MarcadorConsumoAlimentar
  ivcf?: Ivcf
  dataNascimentoCidadao?: LocalDate
  dataAtendimento?: LocalDate
  arquivosAnexados?: ArquivoAtendimentoProfissionalHistorico[]
  mChat?: MChat
}

export default function HistoricoObjetivoPecPanel(props: HistoricoObjetivoPecPanelProps) {
  const {
    evolucaoObjetivo,
    medicoes,
    examesResultados,
    atendProfPreNatal,
    atendProfPuericultura,
    marcadorConsumoAlimentar,
    isAtendObservacao,
    ivcf,
    dataNascimentoCidadao,
    dataAtendimento,
    arquivosAnexados,
    mChat,
  } = props

  const { recentes } = filterByRegistroAnterior<HistoricoMedicaoModel>(medicoes, 'medicaoAnterior')

  const descricao = evolucaoObjetivo?.descricao?.removeTags()

  const possuiInformacoesPuericultura: boolean = atendProfPuericultura && !hasAllAttrsNull(atendProfPuericultura)

  const hasAtendProfPreNatal: boolean =
    atendProfPreNatal?.gravidezPlanejada || atendProfPreNatal?.movimentacaoFetal || !!atendProfPreNatal?.tipoEdema

  const preNatalInfo = getMedicoesPreNatal(recentes)

  const hasPreNatalInfo = hasAtendProfPreNatal || !isEmpty(preNatalInfo)

  const shouldDisplayMCA = !!marcadorConsumoAlimentar

  const { PILOTO_ANEXO_ARQUIVOS_ENABLED, M_CHAT_ENABLED } = useFlags()

  const shouldDisplayArquivosAnexados = !!(PILOTO_ANEXO_ARQUIVOS_ENABLED && arquivosAnexados?.isNotEmpty())

  const shouldDisplayMChat = M_CHAT_ENABLED && mChat

  const hasAgeDates = !isUndefinedOrNull(dataNascimentoCidadao) && !isUndefinedOrNull(dataAtendimento)
  const ageInMonths = hasAgeDates ? calculateAge(dataNascimentoCidadao, dataAtendimento).ageInMonths : null

  const hasObjetivo: boolean =
    !!descricao ||
    !isEmpty(examesResultados) ||
    hasMedicoes(recentes) ||
    hasPreNatalInfo ||
    possuiInformacoesPuericultura ||
    shouldDisplayMCA ||
    !!ivcf ||
    shouldDisplayArquivosAnexados ||
    !!mChat

  return (
    <>
      {hasObjetivo ? (
        <VFlow>
          {descricao && <Text dangerouslySetInnerHTML={{ __html: evolucaoObjetivo.descricao }} />}

          {shouldDisplayArquivosAnexados && <HistoricoArquivosAnexadosTableBox arquivosAnexados={arquivosAnexados} />}

          {hasMedicoes(recentes) &&
            (isAtendObservacao ? (
              <HistoricoMedicoesObservacaoPanel medicoes={recentes} />
            ) : (
              <HistoricoMedicoesTableBox sectionStyle='O' medicoes={recentes} />
            ))}

          {shouldDisplayMCA && <HistoricoMCATableBox marcadorConsumoAlimentar={marcadorConsumoAlimentar} />}

          {ivcf && (
            <HistoricoIvcfTableBox
              ivcf={ivcf}
              riscoVulnerabilidadeText={IVCF_RESULTADOS[ivcf.riscoVulnerabilidade].longTitle}
            />
          )}

          {hasPreNatalInfo && (
            <HistoricoMedicoesPreNatalTableBox medicoes={preNatalInfo} atendProfPreNatal={atendProfPreNatal} />
          )}
          {possuiInformacoesPuericultura && (
            <HistoricoObjetivoInformacoesPuericultura
              medicoes={convertMedicoesToHistoricoMedicaoModel(recentes)}
              atendProfPuericultura={atendProfPuericultura}
            />
          )}
          {shouldDisplayMChat && <HistoricoMChatTableBox mChat={mChat} idadeEmMeses={ageInMonths} />}
          {!isEmpty(examesResultados) && <HistoricoResultadoExamesTable examesResultados={examesResultados} />}
        </VFlow>
      ) : (
        <Text fontStyle='italic'>{naoRegistradoNesseAtendimento}</Text>
      )}
    </>
  )
}
